import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BlockScheduleTemplateModel } from '../../models/blockschedule.model';
import { ProjectService } from '../../services/project.service';

@Component({
  selector: 'app-skidtemplateconfig',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule
  ],
  templateUrl: './skidtemplateconfig.html',
  styleUrl: './skidtemplateconfig.css'
})
export class SkidtemplateconfigComponent implements OnInit {
  templateForm: FormGroup;
  saving: boolean = false;
  projectId: number = 0;

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private projectService: ProjectService,
    private config: DynamicDialogConfig
  ) {
    this.templateForm = this.createForm();
  }

  ngOnInit(): void {
    // Get project ID from dialog data
    if (this.config.data?.projectId) {
      this.projectId = this.config.data.projectId;
      this.templateForm.patchValue({ projectId: this.projectId });
      this.loadTemplate(this.projectId);
    }
  }

  loadTemplate(id: number): void {
    this.projectService.getBlockScheduleTemplate(id).subscribe({
      next: (response) => {
        if (response) {
          console.log('Raw template data from API:', response);

          // Convert date strings to Date objects for PrimeNG DatePicker
          const formData = { ...response };

          // Convert date fields from strings to Date objects
          const dateFields = [
            'monShipDay', 'monArrivalDay',
            'tueShipDay', 'tueArrivalDay',
            'wedShipDay', 'wedArrivalDay',
            'thuShipDay', 'thuArrivalDay',
            'friShipDay', 'friArrivalDay'
          ];

          dateFields.forEach(field => {
            if (formData[field as keyof typeof formData]) {
              const dateValue = formData[field as keyof typeof formData];
              if (typeof dateValue === 'string' && dateValue) {
                const parsedDate = new Date(dateValue);
                // Only set if it's a valid date
                if (!isNaN(parsedDate.getTime())) {
                  (formData as any)[field] = parsedDate;
                }
              }
            }
          });

          console.log('Converted template data for form:', formData);

          // Patch the form with the converted data
          this.templateForm.patchValue(formData);

          // Log the form values after patching to verify the data was set
          console.log('Form values after patching:', this.templateForm.value);
          console.log('Form controls status:', {
            monShipDay: this.templateForm.get('monShipDay')?.value,
            tueShipDay: this.templateForm.get('tueShipDay')?.value,
            wedShipDay: this.templateForm.get('wedShipDay')?.value,
            thuShipDay: this.templateForm.get('thuShipDay')?.value,
            friShipDay: this.templateForm.get('friShipDay')?.value
          });
        }
      },
      error: (error) => {
        console.error('Error loading template:', error);
        alert('Failed to load template data.');
      }
    });
  }

  private createForm(): FormGroup {
    return this.fb.group({
      id: [0],
      projectId: [this.projectId],
      transitDays: [0],

      // Monday
      monShipDay: [null],
      monArrivalDay: [null],
      monSkids: [0],

      // Tuesday  
      tueShipDay: [null],
      tueArrivalDay: [null],
      tueSkids: [0],

      // Wednesday
      wedShipDay: [null],
      wedArrivalDay: [null],
      wedSkids: [0],

      // Thursday
      thuShipDay: [null],
      thuArrivalDay: [null],
      thuSkids: [0],

      // Friday
      friShipDay: [null],
      friArrivalDay: [null],
      friSkids: [0]
    });
  }

  saveTemplate(): void {
    // Early validation check
    if (!this.templateForm.valid) {
      console.log('Form is invalid - marking fields as touched');
      this.markFormGroupTouched();
      return;
    }

    // Prevent double submission
    if (this.saving) {
      return;
    }

    // Basic business rule validation - at least one day should have skids
    const totalSkids = this.getTotalSkids();
    if (totalSkids === 0) {
      console.warn('No skids scheduled for any day');
      alert('Please schedule MV Power Stations for at least one day of the week.');
      return;
    }

    this.saving = true;

    try {
      const templateData: BlockScheduleTemplateModel = {
        ...this.templateForm.value,
        projectId: this.projectId || this.templateForm.value.projectId
      };

      console.log('Saving template:', templateData);

      this.projectService.updateBlockScheduleTemplate(templateData).subscribe({
        next: (response) => {
          console.log('Template saved successfully:', response);
          this.saving = false;
          this.ref.close(response);
        },
        error: (error) => {
          console.error('Error saving template:', error);
          this.saving = false;

          // User-friendly error message
          let errorMessage = 'An error occurred while saving the template.';
          if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          alert(`Failed to save template: ${errorMessage}`);
        }
      });

    } catch (error) {
      console.error('Unexpected error preparing template data:', error);
      this.saving = false;
      alert('An unexpected error occurred. Please try again.');
    }
  }

  cancel(): void {
    this.ref.close();
  }

  getTotalSkids(): number {
    const monSkids = this.templateForm.get('monSkids')?.value || 0;
    const tueSkids = this.templateForm.get('tueSkids')?.value || 0;
    const wedSkids = this.templateForm.get('wedSkids')?.value || 0;
    const thuSkids = this.templateForm.get('thuSkids')?.value || 0;
    const friSkids = this.templateForm.get('friSkids')?.value || 0;

    return monSkids + tueSkids + wedSkids + thuSkids + friSkids;
  }

  getActiveDays(): number {
    let activeDays = 0;

    if (this.templateForm.get('monSkids')?.value > 0) activeDays++;
    if (this.templateForm.get('tueSkids')?.value > 0) activeDays++;
    if (this.templateForm.get('wedSkids')?.value > 0) activeDays++;
    if (this.templateForm.get('thuSkids')?.value > 0) activeDays++;
    if (this.templateForm.get('friSkids')?.value > 0) activeDays++;

    return activeDays;
  }

  getAverageSkidsPerDay(): number {
    const totalSkids = this.getTotalSkids();
    const activeDays = this.getActiveDays();

    return activeDays > 0 ? Math.round((totalSkids / activeDays) * 100) / 100 : 0;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.templateForm.controls).forEach(key => {
      const control = this.templateForm.get(key);
      control?.markAsTouched();
    });
  }


  shipDateChange(day: any, event: any) {
    console.log(event);
    if (event != null) {
      let transitDays = this.templateForm?.get('transitDays')?.value;
      let tsd = this.clone(event);
      this.templateForm.get('tueShipDay')?.setValue(new Date(tsd.setDate(tsd.getDate() + 1)));
      let wsd = this.clone(event);
      this.templateForm.get('wedShipDay')?.setValue(new Date(wsd.setDate(wsd.getDate() + 2)));
      let thsd = this.clone(event);
      this.templateForm.get('thuShipDay')?.setValue(new Date(thsd.setDate(thsd.getDate() + 3)));
      let fsd = this.clone(event);
      this.templateForm.get('friShipDay')?.setValue(new Date(fsd.setDate(fsd.getDate() + 4)));
      let mad = this.clone(event);
      this.templateForm.get('monArrivalDay')?.setValue(new Date(mad.setDate(mad.getDate() + (+transitDays))));
      let tad = this.clone(event);
      this.templateForm.get('tueArrivalDay')?.setValue(new Date(tad.setDate(tad.getDate() + (+transitDays) + 1)));
      let wad = this.clone(event);
      this.templateForm.get('wedArrivalDay')?.setValue(new Date(wad.setDate(wad.getDate() + (+transitDays) + 2)));
      let thad = this.clone(event);
      this.templateForm.get('thuArrivalDay')?.setValue(new Date(thad.setDate(thad.getDate() + (+transitDays) + 3)));
      let fad = this.clone(event);
      this.templateForm.get('friArrivalDay')?.setValue(new Date(fad.setDate(fad.getDate() + (+transitDays) + 4)));
    }
  }

  changeTransitDays(event: any) {
    console.log(event);
    let td = this.templateForm.get('transitDays')?.value;
    if (td != null && td > 0) {
      if (this.templateForm.get('monArrivalDay')?.value) {
        let ms = this.clone(this.templateForm.get('monShipDay')?.value);
        //this.getWorkingDay(ms, td);   
        ms.setDate(ms.getDate() + (+td));
        this.templateForm.get('monArrivalDay')?.setValue(new Date(ms));
      }
      if (this.templateForm.get('tueArrivalDay')?.value) {
        let ts = this.clone(this.templateForm.get('tueShipDay')?.value);
        //this.getWorkingDay(ts, td);
        ts.setDate(ts.getDate() + (+td));
        this.templateForm.get('tueArrivalDay')?.setValue(new Date(ts));
      }
      if (this.templateForm.get('wedArrivalDay')?.value) {
        let ws = this.clone(this.templateForm.get('wedShipDay')?.value);
        // this.getWorkingDay(ws, td);
        ws.setDate(ws.getDate() + (+td));
        this.templateForm.get('wedArrivalDay')?.setValue(new Date(ws));
      }
      if (this.templateForm.get('thuArrivalDay')?.value) {
        let ths = this.clone(this.templateForm.get('thuShipDay')?.value);
        //this.getWorkingDay(ths, td);
        ths.setDate(ths.getDate() + (+td));
        this.templateForm.get('thuArrivalDay')?.setValue(new Date(ths));
      }
      if (this.templateForm.get('friArrivalDay')?.value) {
        let fs = this.clone(this.templateForm.get('friShipDay')?.value);
        //this.getWorkingDay(fs, td);
        fs.setDate(fs.getDate() + (+td));
        this.templateForm.get('friArrivalDay')?.setValue(new Date(fs));
      }
    }
  }
  deliveryDate: Date = new Date;
  enableMondays: number[] = [0, 2, 3, 4, 5, 6];
  enableTuesdays: number[] = [0, 1, 3, 4, 5, 6];
  enableWednesdays: number[] = [0, 1, 2, 4, 5, 6];
  enableThursdays: number[] = [0, 1, 2, 3, 5, 6];
  enableFridays: number[] = [0, 1, 2, 3, 4, 6];

  clone(obj: any) {
    // Handle the 3 simple types, and null or undefined
    if (null == obj || "object" != typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
      var copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }
  }

}
